import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Tag, Space, DatePicker, Select, message, Popconfirm, Card, Statistic, Row, Col, Input, Descriptions, Timeline, Divider, Badge, Image, InputNumber, Alert } from 'antd';
import { SearchOutlined, ReloadOutlined, EyeOutlined, CloseCircleOutlined, DeleteOutlined, DownloadOutlined, CopyOutlined, CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined, StopOutlined, CheckOutlined, CloseOutlined, UploadOutlined } from '@ant-design/icons';
import { adminAPI } from '../services/api';
import { t } from '../utils/i18n';
import dayjs from 'dayjs';
import { Resizable } from 'react-resizable';
import 'react-resizable/css/styles.css';

const { RangePicker } = DatePicker;

// 蛇形命名转驼峰命名
const snakeToCamel = (str) => str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

// 转换订单数据字段命名
const transformOrderData = (orders) => {
  return orders.map(order => {
    const transformed = {};
    for (const key in order) {
      transformed[snakeToCamel(key)] = order[key];
    }
    return transformed;
  });
};

// 可调整宽度的表头组件
const ResizableTitle = (props) => {
  const { onResize, width, ...restProps } = props;

  if (!width) {
    return <th {...restProps} />;
  }

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          style={{
            position: 'absolute',
            right: -5,
            bottom: 0,
            width: 10,
            height: '100%',
            cursor: 'col-resize',
            zIndex: 1,
          }}
          onClick={(e) => e.stopPropagation()}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} />
    </Resizable>
  );
};

const Orders = () => {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]); // 用于导出
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchOrderNo, setSearchOrderNo] = useState('');  // 订单号搜索
  const [searchUserId, setSearchUserId] = useState('');
  const [searchStatus, setSearchStatus] = useState('');
  const [searchRechargeType, setSearchRechargeType] = useState('');  // 充值类型筛选
  const [searchProofUploaded, setSearchProofUploaded] = useState('');  // 凭证上传筛选
  const [dateRange, setDateRange] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);  // 审批加载状态
  const [rejectModalVisible, setRejectModalVisible] = useState(false);  // 拒绝弹窗
  const [rejectReason, setRejectReason] = useState('');  // 拒绝原因

  // 统计数据
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    succeeded: 0,
    failed: 0,
    cancelled: 0,
    totalAmount: 0,
  });

  // 列宽状态
  const [columns, setColumns] = useState([
    {
      title: '订单号',
      dataIndex: 'orderNo',
      key: 'orderNo',
      width: 180,
    },
    {
      title: '用户ID',
      dataIndex: 'userId',
      key: 'userId',
      width: 100,
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
    },
    {
      title: '货币',
      dataIndex: 'currency',
      key: 'currency',
      width: 80,
      render: (text, record) => {
        const pm = record.paymentMethod || record.payment_method;
        const rt = record.rechargeType || record.recharge_type;
        return (rt === 'manual' || pm === 'manual') ? '-' : (text || 'CNY');
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
    },
    {
      title: '充值类型',
      dataIndex: 'rechargeType',
      key: 'rechargeType',
      width: 100,
      render: (text, record) => {
        const rt = text || record.recharge_type;
        if (rt === 'offline') {
          return <Tag color="orange">线下转账</Tag>;
        }
        if (rt === 'manual' || (record.paymentMethod || record.payment_method) === 'manual') {
          return <Tag color="purple">手工调整</Tag>;
        }
        return <Tag color="blue">在线充值</Tag>;
      },
    },
    {
      title: '支付方式',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      width: 110,
      render: (text, record) => {
        const pm = text || record.payment_method;
        console.log('[Orders] payment_method render:', {text, payment_method: record.payment_method, paymentMethod: record.paymentMethod, pm, keys: Object.keys(record).filter(k=>k.includes('pay')||k.includes('method')).join(',')});
        if (pm === 'wechat') return <Tag color="green">微信支付</Tag>;
        if (pm === 'alipay') return <Tag color="blue">支付宝</Tag>;
        if (pm === 'offline') return <Tag>银行转账</Tag>;
        if (pm === 'manual') return <Tag color="purple">手工调整</Tag>;
        return <Tag>{pm || '-'}</Tag>;
      },
    },
    {
      title: '凭证上传',
      key: 'proofUploaded',
      width: 100,
      align: 'center',
      render: (_, record) => {
        const rtype = record.rechargeType || record.recharge_type;
        const pm = record.paymentMethod || record.payment_method;
        // 手工调整不显示凭证状态
        if (rtype === 'manual' || pm === 'manual') return '-';
        // 只有线下订单才显示凭证状态
        if (rtype === 'offline') {
          if (record.transferProofUrl) {
            return <Tag color="success">已上传</Tag>;
          }
          return <Tag color="red">未上传</Tag>;
        }
        return '-';
      },
    },
    {
      title: '转账银行',
      key: 'transferBank',
      width: 150,
      render: (_, record) => {
        if (record.rechargeType === 'offline' && record.transferBank) {
          return (
            <Space>
              <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{record.transferBank}</span>
              <Button 
                type="text" 
                size="small" 
                icon={<CopyOutlined />} 
                onClick={() => copyToClipboard(record.transferBank, '转账银行')}
              />
            </Space>
          );
        }
        return '-';
      },
    },
    {
      title: '用户备注',
      key: 'transferRemark',
      width: 180,
      ellipsis: true,
      render: (_, record) => {
        if (record.rechargeType === 'offline' && record.transferRemark) {
          return (
            <span style={{ fontFamily: 'monospace', fontSize: 12 }} title={record.transferRemark}>
              {record.transferRemark}
            </span>
          );
        }
        return '-';
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
    },
    {
      title: '完成时间',
      dataIndex: 'completedAt',
      key: 'completedAt',
      width: 180,
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
    },
  ]);

  // 处理列宽调整
  const handleResize = (index) => (_, { size }) => {
    const newColumns = [...columns];
    newColumns[index] = {
      ...newColumns[index],
      width: size.width,
    };
    setColumns(newColumns);
  };

  // 合并列宽调整功能到columns
  const mergedColumns = columns.map((col, index) => ({
    ...col,
    onHeaderCell: (column) => ({
      width: column.width,
      onResize: handleResize(index),
    }),
  }));

  useEffect(() => {
    fetchOrders();
  }, [page, pageSize]);

  // 初始加载时获取全局统计数据
  useEffect(() => {
    fetchStats();
  }, []);

  // 搜索时只刷新订单列表，不更新统计
  useEffect(() => {
    if (page === 1) {
      fetchOrders();
    } else {
      setPage(1);
    }
  }, [searchStatus, searchUserId, dateRange, searchOrderNo, searchRechargeType, searchProofUploaded]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchOrderNo) {
        params.order_no = searchOrderNo;
      }
      if (searchUserId) {
        params.user_id = searchUserId;
      }
      if (searchStatus) {
        params.status = searchStatus;
      }
      if (searchRechargeType) {
        params.recharge_type = searchRechargeType;
      }
      if (searchProofUploaded) {
        // 凭证上传筛选：uploaded=有凭证, not_uploaded=无凭证
        if (searchProofUploaded === 'uploaded') {
          params.proof_uploaded = 1;
        } else {
          params.proof_uploaded = 0;
        }
      }
      if (dateRange && dateRange.length === 2) {
        params.start_date = dateRange[0].format('YYYY-MM-DD');
        params.end_date = dateRange[1].format('YYYY-MM-DD');
      }
      
      // 获取当前页数据
      const response = await adminAPI.getOrders(page, pageSize, params);
      if (response.code === 0) {
        // 转换字段命名从蛇形到驼峰
        setOrders(transformOrderData(response.data.list || []));
        setTotal(response.data.total || 0);
      }
    } catch (error) {
      message.error(t('fetch_orders_failed') || '获取订单列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取全局统计数据（不受筛选条件影响）
  const fetchStats = async () => {
    try {
      // 获取各状态的订单数量
      const [pendingRes, succeededRes, failedRes, cancelledRes] = await Promise.all([
        adminAPI.getOrders(1, 1, { status: 'pending' }),
        adminAPI.getOrders(1, 1, { status: 'succeeded' }),
        adminAPI.getOrders(1, 1, { status: 'failed' }),
        adminAPI.getOrders(1, 1, { status: 'cancelled' }),
      ]);
      
      // 获取成功订单来计算总金额
      const succeededTotal = succeededRes.data?.total || 0;
      let totalAmount = 0;
      if (succeededTotal > 0) {
        const succeededOrdersRes = await adminAPI.getOrders(1, Math.min(succeededTotal, 1000), { status: 'succeeded' });
        if (succeededOrdersRes.code === 0) {
          totalAmount = (succeededOrdersRes.data.list || []).reduce((sum, o) => sum + (o.amount || 0), 0);
        }
      }
      
      setStats({
        total: (pendingRes.data?.total || 0) + (succeededRes.data?.total || 0) + (failedRes.data?.total || 0) + (cancelledRes.data?.total || 0),
        pending: pendingRes.data?.total || 0,
        succeeded: succeededRes.data?.total || 0,
        failed: failedRes.data?.total || 0,
        cancelled: cancelledRes.data?.total || 0,
        totalAmount,
      });
    } catch (error) {
      console.error('获取统计数据失败', error);
    }
  };

  // 获取所有订单数据用于导出
  const fetchAllOrdersForExport = async () => {
    try {
      setExportLoading(true);
      const params = {};
      if (searchOrderNo) params.order_no = searchOrderNo;
      if (searchUserId) params.user_id = searchUserId;
      if (searchStatus) params.status = searchStatus;
      if (dateRange && dateRange.length === 2) {
        params.start_date = dateRange[0].format('YYYY-MM-DD');
        params.end_date = dateRange[1].format('YYYY-MM-DD');
      }
      
      // 获取所有数据（假设接口支持大页数）
      const response = await adminAPI.getOrders(1, 10000, params);
      if (response.code === 0) {
        setAllOrders(response.data.list || []);
        return response.data.list || [];
      }
      return [];
    } catch (error) {
      message.error(t('fetch_failed') || '获取数据失败');
      return [];
    } finally {
      setExportLoading(false);
    }
  };

  // 导出为 CSV
  const exportToCSV = async () => {
    const orders = await fetchAllOrdersForExport();
    if (orders.length === 0) {
      message.warning(t('no_data_export') || '没有数据可导出');
      return;
    }

    const headers = ['订单号', '用户ID', '金额', '货币', '状态', 'Payment Intent ID', '创建时间', '完成时间'];
    const rows = orders.map(o => [
      o.order_no || o.id,
      o.user_id,
      o.amount,
      o.currency?.toUpperCase() || 'USD',
      o.status,
      o.stripe_payment_intent_id || '',
      dayjs(o.created_at).format('YYYY-MM-DD HH:mm:ss'),
      o.completed_at ? dayjs(o.completed_at).format('YYYY-MM-DD HH:mm:ss') : '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `orders_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    message.success(t('export_success') || '导出成功');
  };

  // 导出为 Excel（简化版，使用 CSV）
  const handleExport = () => {
    Modal.confirm({
      title: t('confirm_export') || '确认导出',
      content: t('export_orders_tip') || '将导出当前筛选条件下的所有订单数据（CSV 格式）',
      onOk: exportToCSV,
    });
  };

  const handleSearch = () => {
    setPage(1);
    fetchOrders();
  };

  const handleReset = () => {
    setSearchOrderNo('');
    setSearchUserId('');
    setSearchStatus('');
    setSearchRechargeType('');
    setSearchProofUploaded('');
    setDateRange(null);
    setPage(1);
    fetchOrders();
  };

  const handleViewDetail = async (order) => {
    setSelectedOrder(order);
    setDetailModalVisible(true);
  };

  const getStatusTag = (status) => {
    const statusMap = {
      pending: { color: 'processing', text: '处理中', icon: <ClockCircleOutlined /> },
      succeeded: { color: 'success', text: '成功', icon: <CheckCircleOutlined /> },
      failed: { color: 'error', text: '失败', icon: <ExclamationCircleOutlined /> },
      cancelled: { color: 'default', text: '已取消', icon: <StopOutlined /> },
    };
    const config = statusMap[status] || { color: 'default', text: status, icon: null };
    return <Badge status={config.color} text={config.text} />;
  };

  // 取消订单
  const handleCancelOrder = async (order) => {
    try {
      const response = await adminAPI.cancelOrder(order.id);
      if (response.code === 0) {
        message.success('订单已取消');
        fetchOrders();
      } else {
        message.error(response.message || '取消失败');
      }
    } catch (error) {
      message.error('取消订单失败');
    }
  };

  // 删除订单
  const handleDeleteOrder = async (order) => {
    try {
      const response = await adminAPI.deleteOrder(order.id);
      if (response.code === 0) {
        message.success('订单已删除');
        fetchOrders();
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error) {
      message.error('删除订单失败');
    }
  };

  // 复制到剪贴板
  const copyToClipboard = (text, label = '内容') => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(`${label}已复制到剪贴板`);
    }).catch(() => {
      message.error('复制失败');
    });
  };

  // 审核通过线下订单
  const handleApproveOfflineOrder = async (order) => {
    setApproveLoading(true);
    try {
      const response = await adminAPI.approveOfflineOrder(order.orderNo, '');
      if (response.code === 0) {
        message.success('审核通过，余额已增加给用户');
        fetchOrders();
        fetchStats();
        setDetailModalVisible(false);
      } else {
        message.error(response.message || '审核失败');
      }
    } catch (error) {
      console.error('Approve error:', error);
      message.error('审核失败');
    } finally {
      setApproveLoading(false);
    }
  };

  // 拒绝线下订单
  const handleRejectOfflineOrder = async () => {
    if (!rejectReason.trim()) {
      message.warning('请填写拒绝原因');
      return;
    }
    setApproveLoading(true);
    try {
      const response = await adminAPI.rejectOfflineOrder(selectedOrder?.orderNo, rejectReason);
      if (response.code === 0) {
        message.success('已拒绝该订单');
        fetchOrders();
        fetchStats();
        setDetailModalVisible(false);
        setRejectModalVisible(false);
        setRejectReason('');
      } else {
        message.error(response.message || '拒绝失败');
      }
    } catch (error) {
      console.error('Reject error:', error);
      message.error('拒绝失败');
    } finally {
      setApproveLoading(false);
    }
  };

  // 判断是否是线下待审核订单
  const isOfflinePendingOrder = (order) => {
    return order?.rechargeType === 'offline' && 
           order?.paymentMethod === 'offline' && 
           order?.status === 'pending';
  };

  // 获取完整的图片 URL — 生产环境使用相对路径，nginx 代理 /uploads/ 到 hk-api
  const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    // 开发环境：直连 localhost:18080，生产环境：相对路径由 nginx 代理
    if (process.env.NODE_ENV === 'production') {
      return path;
    }
    const base = process.env.REACT_APP_API_URL || 'http://localhost:18080';
    return `${base}${path}`;
  };

  // 列渲染函数
  const isManual = (record) => (record.rechargeType || record.recharge_type) === 'manual' || (record.paymentMethod || record.payment_method) === 'manual';

  const renderCell = (column, record) => {
    const manual = isManual(record);
    // 凭证上传列
    if (column.key === 'proofUploaded') {
      if (manual) return '-';
      if (record.rechargeType === 'offline') {
        if (record.transferProofUrl) {
          return <Tag color="success">已上传</Tag>;
        }
        return <Tag color="red">未上传</Tag>;
      }
      return '-';
    }

    // 转账银行列
    if (column.key === 'transferBank') {
      if (manual) return '-';
      if (record.rechargeType === 'offline' && record.transferBank) {
        return (
          <Space>
            <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{record.transferBank}</span>
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyToClipboard(record.transferBank, '转账银行')}
            />
          </Space>
        );
      }
      return '-';
    }

    // 用户备注列
    if (column.key === 'transferRemark') {
      if (manual) return '-';
      if (record.rechargeType === 'offline' && record.transferRemark) {
        return (
          <span style={{ fontFamily: 'monospace', fontSize: 12 }} title={record.transferRemark}>
            {record.transferRemark}
          </span>
        );
      }
      return '-';
    }

    // 充值类型列
    if (column.key === 'rechargeType') {
      if (record.rechargeType === 'offline') {
        return <Tag color="orange">线下转账</Tag>;
      }
      return <Tag color="blue">在线充值</Tag>;
    }

    // 支付方式列
    if (column.key === 'paymentMethod') {
      const pm = record.paymentMethod || record.payment_method;
      if (pm === 'wechat') return <Tag color="green">微信支付</Tag>;
      if (pm === 'alipay') return <Tag color="blue">支付宝</Tag>;
      if (pm === 'offline') return <Tag>银行转账</Tag>;
      if (pm === 'manual') return <Tag color="purple">手工调整</Tag>;
      return <Tag>{pm || '-'}</Tag>;
    }

    switch (column.key) {
      case 'orderNo':
        return (
          <span style={{ fontFamily: 'monospace', fontSize: 13 }}>
            {record.orderNo || record.id}
          </span>
        );
      case 'userId':
        return <span style={{ fontFamily: 'monospace' }}>{record.userId}</span>;
      case 'amount':
        return (
          <span style={{ fontWeight: 600, color: '#52c41a' }}>
            ¥{record.amount} {manual ? 'CNY' : (record.currency?.toUpperCase() || 'USD')}
          </span>
        );
      case 'currency':
        return manual ? 'CNY' : (record.currency?.toUpperCase() || 'USD');
      case 'status':
        return getStatusTag(record.status);
      case 'createdAt':
        return dayjs(record.createdAt).format('YYYY-MM-DD HH:mm:ss');
      case 'completedAt':
        return record.completedAt ? dayjs(record.completedAt).format('YYYY-MM-DD HH:mm:ss') : '-';
      case 'action':
        return (
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>
        );
      default:
        return record[column.dataIndex];
    }
  };

  const columnsWithRender = mergedColumns.map(col => ({
    ...col,
    render: (_, record) => renderCell(col, record),
  }));

  return (
    <div className="fit-screen-page">
      {/* 功能区 */}
      <div className="page-header">
        <h2 className="page-title">{t('order_management') || '订单管理'}</h2>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={4}>
          <Card size="small">
            <Statistic title={t('total_orders') || '总订单'} value={stats.total} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title={t('pending_orders') || '处理中'} value={stats.pending} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title={t('succeeded_orders') || '成功'} value={stats.succeeded} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title={t('failed_orders') || '失败'} value={stats.failed} valueStyle={{ color: '#ff4d4f' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title={t('cancelled_orders') || '已取消'} value={stats.cancelled} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title={t('total_amount') || '总金额'} value={`¥${stats.totalAmount.toFixed(2)}`} />
          </Card>
        </Col>
      </Row>

      {/* 搜索栏 */}
      <div className="search-bar" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <Input.Search
          placeholder="订单号"
          value={searchOrderNo}
          onChange={(e) => setSearchOrderNo(e.target.value)}
          onSearch={handleSearch}
          style={{ width: 200 }}
          allowClear
          prefix={<span style={{ fontSize: 12, color: '#999' }}>#</span>}
        />
        <Input
          placeholder="用户ID"
          value={searchUserId}
          onChange={(e) => setSearchUserId(e.target.value)}
          onPressEnter={handleSearch}
          style={{ width: 120 }}
          allowClear
        />
        <Select
          placeholder="订单状态"
          value={searchStatus || undefined}
          onChange={(value) => { setSearchStatus(value); handleSearch(); }}
          style={{ width: 120 }}
          allowClear
        >
          <Select.Option value="pending">处理中</Select.Option>
          <Select.Option value="succeeded">成功</Select.Option>
          <Select.Option value="failed">失败</Select.Option>
          <Select.Option value="cancelled">已取消</Select.Option>
        </Select>
        <Select
          placeholder="充值类型"
          value={searchRechargeType || undefined}
          onChange={(value) => { setSearchRechargeType(value); handleSearch(); }}
          style={{ width: 120 }}
          allowClear
        >
          <Select.Option value="online">在线充值</Select.Option>
          <Select.Option value="offline">线下转账</Select.Option>
        </Select>
        <Select
          placeholder="凭证上传"
          value={searchProofUploaded || undefined}
          onChange={(value) => { setSearchProofUploaded(value); handleSearch(); }}
          style={{ width: 120 }}
          allowClear
        >
          <Select.Option value="uploaded">已上传</Select.Option>
          <Select.Option value="not_uploaded">未上传</Select.Option>
        </Select>
        <DatePicker.RangePicker
          value={dateRange}
          onChange={setDateRange}
          style={{ width: 250 }}
          placeholder={['开始日期', '结束日期']}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
          {t('search') || '搜索'}
        </Button>
        <Button icon={<ReloadOutlined />} onClick={handleReset}>
          {t('reset') || '重置'}
        </Button>
        <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport} loading={exportLoading} style={{ marginLeft: 'auto' }}>
          {t('export') || '导出'}
        </Button>
      </div>

      {/* 表格容器 */}
      <div className="table-container">
        <Table
          columns={columnsWithRender}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          scroll={{ x: 'max-content', y: 600 }}
          components={{
            header: {
              cell: ResizableTitle,
            },
          }}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: (page, pageSize) => {
              setPage(page);
              setPageSize(pageSize);
            },
          }}
        />
      </div>

      {/* 订单详情对话框 - 专业设计 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>订单详情</span>
            {selectedOrder && (
              <Tag color={selectedOrder.status === 'succeeded' ? 'success' : selectedOrder.status === 'pending' ? 'processing' : selectedOrder.status === 'failed' ? 'error' : 'default'}>
                {selectedOrder.status === 'succeeded' ? '已支付' : selectedOrder.status === 'pending' ? '处理中' : selectedOrder.status === 'failed' ? '支付失败' : '已取消'}
              </Tag>
            )}
            {selectedOrder?.rechargeType === 'offline' && selectedOrder?.paymentMethod === 'offline' && (
              <Tag color="orange">银行转账</Tag>
            )}
          </div>
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={
          <Space>
            {/* 线下待审核订单显示审核按钮 */}
            {isOfflinePendingOrder(selectedOrder) ? (
              <>
                <Popconfirm
                  title="确认审核通过此订单？"
                  description="审核通过后，系统将自动增加用户余额"
                  onConfirm={() => handleApproveOfflineOrder(selectedOrder)}
                  okText="确认通过"
                  cancelText="取消"
                  okButtonProps={{ loading: approveLoading }}
                >
                  <Button type="primary" icon={<CheckOutlined />} loading={approveLoading}>
                    审核通过
                  </Button>
                </Popconfirm>
                <Button danger icon={<CloseOutlined />} onClick={() => setRejectModalVisible(true)}>
                  审核拒绝
                </Button>
              </>
            ) : selectedOrder && selectedOrder.status === 'pending' ? (
              <>
                <Popconfirm
                  title="确认取消此订单？"
                  description="取消后订单将无法恢复"
                  onConfirm={() => {
                    handleCancelOrder(selectedOrder);
                    setDetailModalVisible(false);
                  }}
                  okText="确认取消"
                  cancelText="暂不"
                  okButtonProps={{ danger: true }}
                >
                  <Button danger icon={<CloseCircleOutlined />}>取消订单</Button>
                </Popconfirm>
                <Popconfirm
                  title="确认删除此订单？"
                  description="删除后订单将无法恢复"
                  onConfirm={() => {
                    handleDeleteOrder(selectedOrder);
                    setDetailModalVisible(false);
                  }}
                  okText="确认删除"
                  cancelText="暂不"
                  okButtonProps={{ danger: true }}
                >
                  <Button danger type="text" icon={<DeleteOutlined />}>删除</Button>
                </Popconfirm>
              </>
            ) : null}
            <Button onClick={() => setDetailModalVisible(false)}>关闭</Button>
          </Space>
        }
        width={720}
        centered
      >
        {selectedOrder && (
          <div style={{ padding: '8px 0' }}>
            {/* 订单金额展示 */}
            <div style={{ 
              textAlign: 'center', 
              padding: '24px 0', 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 12,
              marginBottom: 24,
              color: '#fff'
            }}>
              <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 8 }}>支付金额</div>
              <div style={{ fontSize: 36, fontWeight: 700 }}>
                ¥{selectedOrder.amount}
                <span style={{ fontSize: 18, marginLeft: 4 }}>{selectedOrder.currency?.toUpperCase() || 'CNY'}</span>
              </div>
            </div>

            {/* 基础信息 */}
            <Descriptions column={2} size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="订单号">
                <Space>
                  <span style={{ fontFamily: 'monospace' }}>{selectedOrder.orderNo || selectedOrder.id}</span>
                  <Button 
                    type="text" 
                    size="small" 
                    icon={<CopyOutlined />} 
                    onClick={() => copyToClipboard(selectedOrder.orderNo || selectedOrder.id, '订单号')}
                  />
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="用户ID">
                <Space>
                  <span style={{ fontFamily: 'monospace' }}>{selectedOrder.userId}</span>
                  <Button 
                    type="text" 
                    size="small" 
                    icon={<CopyOutlined />} 
                    onClick={() => copyToClipboard(selectedOrder.userId, '用户ID')}
                  />
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="订单状态">
                {getStatusTag(selectedOrder.status)}
              </Descriptions.Item>
              <Descriptions.Item label="支付方式">
                {selectedOrder.paymentMethod === 'wechat' ? '微信支付' :
                 selectedOrder.paymentMethod === 'alipay' ? '支付宝' :
                 selectedOrder.paymentMethod === 'stripe' ? 'Stripe' :
                 selectedOrder.paymentMethod === 'paypal' ? 'PayPal' :
                 selectedOrder.paymentMethod === 'fastspring' ? 'FastSpring' :
                 selectedOrder.paymentMethod === 'offline' ? '银行转账' :
                 selectedOrder.paymentMethod || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="充值类型">
                {selectedOrder.rechargeType === 'offline' ? '线下充值' : '在线充值'}
              </Descriptions.Item>
            </Descriptions>

            <Divider style={{ margin: '16px 0' }} />

            {/* 线下转账凭证信息 */}
            {selectedOrder.rechargeType === 'offline' && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 13, color: '#666', marginBottom: 12, fontWeight: 500 }}>转账凭证信息</div>
                  <Descriptions column={2} size="small">
                    {selectedOrder.transferBank && (
                      <Descriptions.Item label="转账银行">{selectedOrder.transferBank}</Descriptions.Item>
                    )}
                    {selectedOrder.transferTime && (
                      <Descriptions.Item label="转账时间">
                        {dayjs(selectedOrder.transferTime).format('YYYY-MM-DD HH:mm:ss')}
                      </Descriptions.Item>
                    )}
                    {selectedOrder.transferRemark && (
                      <Descriptions.Item label="用户备注" span={2}>
                        {selectedOrder.transferRemark}
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                  
                  {/* 凭证图片 */}
                  {selectedOrder.transferProofUrl ? (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>转账凭证截图：</div>
                      <Image 
                        src={getImageUrl(selectedOrder.transferProofUrl)} 
                        alt="转账凭证"
                        style={{ maxWidth: 300, border: '1px solid #e8e8e8', borderRadius: 4 }}
                      />
                    </div>
                  ) : (
                    <Alert 
                      message="用户尚未上传转账凭证" 
                      description="请等待用户上传凭证后再进行审核" 
                      type="warning" 
                      showIcon 
                      style={{ marginTop: 12 }} 
                    />
                  )}
                </div>

                {/* 审核信息 */}
                {selectedOrder.reviewedAt && (
                  <Descriptions column={2} size="small" style={{ marginBottom: 16 }}>
                    <Descriptions.Item label="审核人">{selectedOrder.reviewedBy || '-'}</Descriptions.Item>
                    <Descriptions.Item label="审核时间">
                      {dayjs(selectedOrder.reviewedAt).format('YYYY-MM-DD HH:mm:ss')}
                    </Descriptions.Item>
                    {selectedOrder.adminNotes && (
                      <Descriptions.Item label="审核备注" span={2}>
                        {selectedOrder.adminNotes}
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                )}

              <Divider style={{ margin: '16px 0' }} />
              </>
            )}
            
            {/* 支付信息 - 只在 Stripe 支付时显示 */}
            {selectedOrder.paymentMethod === 'stripe' && (selectedOrder.stripePaymentIntentId || selectedOrder.stripeEventId) && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: '#666', marginBottom: 12, fontWeight: 500 }}>支付信息</div>
                <Descriptions column={1} size="small">
                  {selectedOrder.stripePaymentIntentId && (
                    <Descriptions.Item label="Payment Intent ID">
                      <Space>
                        <span style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                          {selectedOrder.stripePaymentIntentId}
                        </span>
                        <Button 
                          type="text" 
                          size="small" 
                          icon={<CopyOutlined />} 
                          onClick={() => copyToClipboard(selectedOrder.stripePaymentIntentId, 'Payment Intent ID')}
                        />
                      </Space>
                    </Descriptions.Item>
                  )}
                  {selectedOrder.stripeEventId && (
                    <Descriptions.Item label="Stripe Event ID">
                      <Space>
                        <span style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                          {selectedOrder.stripeEventId}
                        </span>
                        <Button 
                          type="text" 
                          size="small" 
                          icon={<CopyOutlined />} 
                          onClick={() => copyToClipboard(selectedOrder.stripeEventId, 'Event ID')}
                        />
                      </Space>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </div>
            )}

            <Divider style={{ margin: '16px 0' }} />

            {/* 时间线 */}
            <div>
              <div style={{ fontSize: 13, color: '#666', marginBottom: 12, fontWeight: 500 }}>订单时间线</div>
              <Timeline
                items={[
                  {
                    color: 'blue',
                    children: (
                      <div>
                        <div style={{ fontWeight: 500 }}>订单创建</div>
                        <div style={{ fontSize: 12, color: '#999' }}>
                          {dayjs(selectedOrder.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                        </div>
                      </div>
                    ),
                  },
                  ...(selectedOrder.status === 'succeeded' && selectedOrder.completedAt ? [{
                    color: 'green',
                    children: (
                      <div>
                        <div style={{ fontWeight: 500 }}>支付完成</div>
                        <div style={{ fontSize: 12, color: '#999' }}>
                          {dayjs(selectedOrder.completedAt).format('YYYY-MM-DD HH:mm:ss')}
                        </div>
                      </div>
                    ),
                  }] : []),
                  ...(selectedOrder.status === 'failed' ? [{
                    color: 'red',
                    children: (
                      <div>
                        <div style={{ fontWeight: 500 }}>支付失败</div>
                        <div style={{ fontSize: 12, color: '#999' }}>
                          {dayjs(selectedOrder.updatedAt || selectedOrder.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                        </div>
                      </div>
                    ),
                  }] : []),
                  ...(selectedOrder.status === 'cancelled' ? [{
                    color: 'gray',
                    children: (
                      <div>
                        <div style={{ fontWeight: 500 }}>订单取消</div>
                        <div style={{ fontSize: 12, color: '#999' }}>
                          {dayjs(selectedOrder.updatedAt || selectedOrder.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                        </div>
                      </div>
                    ),
                  }] : []),
                ]}
              />
            </div>
          </div>
        )}
      </Modal>

      {/* 拒绝原因弹窗 */}
      <Modal
        title="拒绝订单"
        open={rejectModalVisible}
        onCancel={() => {
          setRejectModalVisible(false);
          setRejectReason('');
        }}
        onOk={handleRejectOfflineOrder}
        okText="确认拒绝"
        cancelText="取消"
        okButtonProps={{ danger: true, loading: approveLoading }}
      >
        <div style={{ marginBottom: 16 }}>
          <Alert
            message="拒绝此订单后，用户的余额不会增加"
            description="请务必填写拒绝原因，以便用户了解情况"
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <div style={{ fontSize: 13, marginBottom: 8 }}>拒绝原因（必填）：</div>
          <Input.TextArea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="请输入拒绝原因"
            rows={4}
          />
        </div>
      </Modal>
    </div>
  );
};

export default Orders;
